// src/pages/EditClass.jsx
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeftIcon,
  BookOpenIcon,
  AcademicCapIcon,
  CheckIcon,
  XMarkIcon,
  InformationCircleIcon,
} from '@heroicons/react/24/outline';
import { toast } from 'react-hot-toast';
import apiClient from '../api/client';
import { FireLoader } from '../components/Loader';

export default function EditClass() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    level: 'primary',
    class_number: '',
    faculty: 'general',
    description: '',
    status: 'active',
  });

  const levels = [
    { value: 'nursery', label: 'Nursery' },
    { value: 'primary', label: 'Primary' },
    { value: 'jss', label: 'JSS' },
    { value: 'sss', label: 'SSS' },
  ];

  const faculties = [
    { value: 'general', label: 'General' },
    { value: 'science', label: 'Science' },
    { value: 'arts', label: 'Arts' },
    { value: 'commerce', label: 'Commerce' },
  ];

  const statuses = [
    { value: 'active', label: 'Active' },
    { value: 'inactive', label: 'Inactive' },
    { value: 'archived', label: 'Archived' },
  ];

  useEffect(() => {
    fetchClassData();
  }, [id]);

  const fetchClassData = async () => {
    try {
      setLoading(true);
      const response = await apiClient.get(`/classes/${id}`);
      const data = response.data.data || response.data;
      
      setFormData({
        name: data.name || '',
        level: data.level || 'primary',
        class_number: data.class_number || '',
        faculty: data.faculty || 'general',
        description: data.description || '',
        status: data.status || 'active',
      });
    } catch (error) {
      toast.error('Failed to fetch class data');
      console.error('Error fetching class:', error);
      navigate('/app/classes');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name.trim()) {
      toast.error('Class name is required');
      return;
    }
    if (!formData.class_number.trim()) {
      toast.error('Class number is required');
      return;
    }
    if (isNaN(formData.class_number)) {
      toast.error('Class number must be a valid number');
      return;
    }

    try {
      setSaving(true);
      await apiClient.put(`/classes/${id}`, {
        ...formData,
        class_number: formData.class_number.trim()
      });
      
      toast.success('Class updated successfully!');
      navigate('/app/classes');
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to update class');
      console.error('Error updating class:', error);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <FireLoader fullScreen={true} size="lg" text="LOADING CLASS..." />;
  }

  return (
    <div className="max-w-4xl mx-auto space-y-5 sm:space-y-6 animate-fade-in px-4 sm:px-0">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4">
        <button
          onClick={() => navigate('/app/classes')}
          className="p-2 rounded-xl hover:bg-primary-50 text-primary-600 transition-colors self-start"
        >
          <ArrowLeftIcon className="w-5 h-5" />
        </button>
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-primary-700">Edit Class</h1>
          <p className="text-sm sm:text-base text-primary-400 mt-0.5 sm:mt-1">Update class details and information</p>
        </div>
      </div>

      {/* Form */}
      <div className="bg-white rounded-2xl shadow-sm border border-primary-50/50 p-4 sm:p-6">
        <form onSubmit={handleSubmit} className="space-y-5 sm:space-y-6">
          {/* Quick Tips */}
          <div className="bg-blue-50 rounded-xl p-3 sm:p-4 border border-blue-100">
            <div className="flex items-start gap-3">
              <InformationCircleIcon className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5" />
              <div className="text-sm text-blue-700">
                <p className="font-medium">Editing Tips:</p>
                <ul className="list-disc list-inside space-y-1 mt-1 text-blue-600 text-xs sm:text-sm">
                  <li>Update the class name or number as needed</li>
                  <li>Changing the level will affect how the class is organized</li>
                  <li>Faculty is especially important for SSS level classes</li>
                </ul>
              </div>
            </div>
          </div>

          {/* Class Name */}
          <div>
            <label className="block text-sm font-medium text-primary-600 mb-1.5 sm:mb-2">
              Class Name <span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleChange}
                placeholder="e.g., PRIMARY 1, JSS 2, SSS 3 Science"
                className="w-full px-4 py-2.5 rounded-xl border border-primary-200/50 focus:border-primary-400 focus:ring-2 focus:ring-primary-200/50 outline-none bg-primary-50/30 text-primary-700 transition-all text-sm sm:text-base"
              />
              <BookOpenIcon className="absolute right-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-primary-400" />
            </div>
            <p className="text-xs text-primary-400 mt-1">This will be displayed as the class name throughout the system</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-5 sm:gap-6">
            {/* Level */}
            <div>
              <label className="block text-sm font-medium text-primary-600 mb-1.5 sm:mb-2">
                Level <span className="text-red-500">*</span>
              </label>
              <select
                name="level"
                value={formData.level}
                onChange={handleChange}
                className="w-full px-4 py-2.5 rounded-xl border border-primary-200/50 focus:border-primary-400 focus:ring-2 focus:ring-primary-200/50 outline-none bg-primary-50/30 text-primary-700 transition-all text-sm sm:text-base"
              >
                {levels.map(level => (
                  <option key={level.value} value={level.value}>
                    {level.label}
                  </option>
                ))}
              </select>
              <p className="text-xs text-primary-400 mt-1">Select the education level for this class</p>
            </div>

            {/* Class Number */}
            <div>
              <label className="block text-sm font-medium text-primary-600 mb-1.5 sm:mb-2">
                Class Number <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <input
                  type="number"
                  name="class_number"
                  value={formData.class_number}
                  onChange={handleChange}
                  placeholder="e.g., 1, 2, 3"
                  min="1"
                  max="6"
                  className="w-full px-4 py-2.5 rounded-xl border border-primary-200/50 focus:border-primary-400 focus:ring-2 focus:ring-primary-200/50 outline-none bg-primary-50/30 text-primary-700 transition-all text-sm sm:text-base"
                />
                <AcademicCapIcon className="absolute right-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-primary-400" />
              </div>
              <p className="text-xs text-primary-400 mt-1">The class number within its level (e.g., 1 for Primary 1)</p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-5 sm:gap-6">
            {/* Faculty */}
            <div>
              <label className="block text-sm font-medium text-primary-600 mb-1.5 sm:mb-2">
                Faculty / Department
              </label>
              <select
                name="faculty"
                value={formData.faculty}
                onChange={handleChange}
                className="w-full px-4 py-2.5 rounded-xl border border-primary-200/50 focus:border-primary-400 focus:ring-2 focus:ring-primary-200/50 outline-none bg-primary-50/30 text-primary-700 transition-all text-sm sm:text-base"
              >
                {faculties.map(faculty => (
                  <option key={faculty.value} value={faculty.value}>
                    {faculty.label}
                  </option>
                ))}
              </select>
              <p className="text-xs text-primary-400 mt-1">For SSS level, choose Science, Arts, or Commerce</p>
            </div>

            {/* Status */}
            <div>
              <label className="block text-sm font-medium text-primary-600 mb-1.5 sm:mb-2">
                Status
              </label>
              <select
                name="status"
                value={formData.status}
                onChange={handleChange}
                className="w-full px-4 py-2.5 rounded-xl border border-primary-200/50 focus:border-primary-400 focus:ring-2 focus:ring-primary-200/50 outline-none bg-primary-50/30 text-primary-700 transition-all text-sm sm:text-base"
              >
                {statuses.map(status => (
                  <option key={status.value} value={status.value}>
                    {status.label}
                  </option>
                ))}
              </select>
              <p className="text-xs text-primary-400 mt-1">Inactive classes won't be displayed in active student enrollment</p>
            </div>
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-medium text-primary-600 mb-1.5 sm:mb-2">
              Description
            </label>
            <textarea
              name="description"
              value={formData.description || ''}
              onChange={handleChange}
              rows="3"
              placeholder="Add any additional information about this class..."
              className="w-full px-4 py-2.5 rounded-xl border border-primary-200/50 focus:border-primary-400 focus:ring-2 focus:ring-primary-200/50 outline-none bg-primary-50/30 text-primary-700 resize-none transition-all text-sm sm:text-base"
            />
            <p className="text-xs text-primary-400 mt-1">Optional description to provide more context about the class</p>
          </div>

          {/* Preview Section */}
          <div className="bg-primary-50/30 rounded-xl p-3 sm:p-4 border border-primary-100">
            <h3 className="text-sm font-medium text-primary-600 mb-2">Class Preview</h3>
            <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5 text-sm">
              <span className="text-primary-400">Name:</span>
              <span className="font-medium text-primary-700">
                {formData.name || 'Class Name'}
              </span>
              <span className="text-primary-300 hidden sm:inline">|</span>
              <span className="text-primary-400">Level:</span>
              <span className="font-medium text-primary-700">
                {levels.find(l => l.value === formData.level)?.label || 'Primary'}
              </span>
              <span className="text-primary-300 hidden sm:inline">|</span>
              <span className="text-primary-400">Class:</span>
              <span className="font-medium text-primary-700">
                {formData.class_number || '#'}
              </span>
              <span className="text-primary-300 hidden sm:inline">|</span>
              <span className="text-primary-400">Faculty:</span>
              <span className="font-medium text-primary-700">
                {faculties.find(f => f.value === formData.faculty)?.label || 'General'}
              </span>
            </div>
          </div>

          {/* Form Actions */}
          <div className="flex flex-col sm:flex-row gap-3 pt-4 border-t border-primary-100">
            <button
              type="button"
              onClick={() => navigate('/app/classes')}
              className="flex-1 px-4 py-2.5 border border-primary-200 rounded-xl hover:bg-primary-50 transition-colors text-primary-700 flex items-center justify-center gap-2 text-sm sm:text-base"
            >
              <XMarkIcon className="w-5 h-5" />
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              className="flex-1 px-4 py-2.5 bg-gradient-to-r from-primary-500 to-secondary-500 text-white rounded-xl hover:shadow-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 text-sm sm:text-base"
            >
              {saving ? (
                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
              ) : (
                <>
                  <CheckIcon className="w-5 h-5" />
                  Update Class
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}